import React from "react";

/**
 * Global error boundary — catches any React render-time error and shows a
 * readable error screen with the stack trace, instead of a silent blank page.
 *
 * Without this, a runtime error in any component (e.g. accessing a property
 * of undefined data, an old Safari that fails to parse a syntax feature, etc.)
 * crashes the whole React tree and leaves the user looking at a white screen
 * with no information.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => {
    this.setState({ error: null, info: null });
    // Force a soft reload of the route the user is on
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "n/a";
    const message = String(this.state.error?.message || this.state.error);
    const stack = String(this.state.error?.stack || "");
    const compStack = String(this.state.info?.componentStack || "");

    return (
      <div
        style={{
          minHeight: "100vh", padding: "32px 24px",
          background: "#FAF8F3", color: "#1A1A18",
          fontFamily: "system-ui, -apple-system, Helvetica, sans-serif",
        }}
        data-testid="error-boundary"
      >
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "#4A7A3D", letterSpacing: "0.1em", marginBottom: 16 }}>
            RWANA BIZIN LEGA — ERREUR D'AFFICHAGE
          </div>
          <h1 style={{ fontSize: 36, fontWeight: 700, marginBottom: 16 }}>
            Une erreur est survenue
          </h1>
          <p style={{ fontSize: 16, color: "#4B4B48", lineHeight: 1.6, marginBottom: 24 }}>
            La page n'a pas pu s'afficher correctement. Essayez de recharger.
            Si le problème persiste, transmettez ce diagnostic à l'équipe technique.
          </p>
          <button
            type="button"
            onClick={this.reset}
            style={{
              background: "#4A7A3D", color: "#FAF8F3",
              padding: "12px 24px", border: "none",
              fontFamily: "monospace", fontSize: 12, letterSpacing: "0.1em",
              cursor: "pointer", marginBottom: 32,
            }}
            data-testid="error-boundary-reload"
          >
            RECHARGER LA PAGE
          </button>

          <details open style={{ borderTop: "2px solid #1A1A18", paddingTop: 24, fontSize: 13 }}>
            <summary style={{ fontFamily: "monospace", letterSpacing: "0.1em", color: "#4A7A3D", cursor: "pointer", marginBottom: 16 }}>
              DIAGNOSTIC TECHNIQUE
            </summary>
            <div style={{ marginBottom: 8 }}>
              <strong>Message :</strong>
              <pre style={{ background: "#F5EFEB", padding: 12, overflow: "auto", whiteSpace: "pre-wrap" }}>{message}</pre>
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Navigateur :</strong>
              <pre style={{ background: "#F5EFEB", padding: 12, overflow: "auto", whiteSpace: "pre-wrap" }}>{ua}</pre>
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>URL :</strong>
              <pre style={{ background: "#F5EFEB", padding: 12, overflow: "auto", whiteSpace: "pre-wrap" }}>{typeof window !== "undefined" ? window.location.href : "n/a"}</pre>
            </div>
            {compStack && (
              <div style={{ marginBottom: 8 }}>
                <strong>Composant React :</strong>
                <pre style={{ background: "#F5EFEB", padding: 12, overflow: "auto", whiteSpace: "pre-wrap", maxHeight: 200 }}>{compStack}</pre>
              </div>
            )}
            {stack && (
              <div>
                <strong>Stack :</strong>
                <pre style={{ background: "#F5EFEB", padding: 12, overflow: "auto", whiteSpace: "pre-wrap", maxHeight: 300 }}>{stack}</pre>
              </div>
            )}
          </details>
        </div>
      </div>
    );
  }
}
